import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Copy, Check, Upload, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { pickLangText } from "@/i18n";
import { serializeLifeEvents } from "@/components/LifeEvents";

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// If admin entered just a phone number (no @bank suffix), append @paytm so that
// UPI intent apps can resolve it. Users with a proper VPA (e.g. "abc@ybl")
// will pass unchanged.
function normaliseVpa(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (s.includes("@")) return s;
  // strip anything non-digit for bare-number VPAs
  const digits = s.replace(/\D/g, "");
  return digits ? `${digits}@paytm` : "";
}

const Payment = ({ settings }) => {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const { state } = useLocation();

  const pkg = state?.pkg;
  const form = state?.form;

  const [txnRef, setTxnRef] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const rawUpi = settings?.upi_number || "9462029820";
  const upiVpa = useMemo(() => normaliseVpa(rawUpi), [rawUpi]);
  const upiName = settings?.upi_name || "Sarika Sharma";
  const amount = pkg?.amount || 0;

  const upiLink = useMemo(() => {
    // upi payment link: upi://pay?pa=<VPA>&pn=<name>&am=<amount>&cu=INR
    const params = new URLSearchParams({
      pa: upiVpa,
      pn: upiName,
      am: String(amount),
      cu: "INR",
      tn: `AstroDev ${pkg?.id || ""}`.trim(),
    });
    return `upi://pay?${params.toString()}`;
  }, [upiVpa, upiName, amount, pkg]);

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(upiVpa);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  // best-effort mobile detection so we only surface the "Open UPI app"
  // intent link on devices where a UPI handler actually exists.
  const isMobile = typeof navigator !== "undefined" &&
    /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

  if (!pkg || !form) {
    return (
      <div className="section container-narrow">
        <div className="notice error">
          Missing booking information.{" "}
          <Link to="/booking" className="underline">
            Go back
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      let base64 = null;
      if (file) {
        base64 = await readFileAsBase64(file);
      }
      const body = {
        package_id: pkg.id,
        package_label: pickLangText(pkg, "en", "title"),
        package_amount: pkg.amount,
        full_name: form.full_name,
        gender: form.gender,
        contact: form.contact,
        email: form.email || null,
        dob: form.dob,
        time_of_birth: form.time_of_birth,
        place_of_birth: form.place_of_birth,
        questions: form.questions || null,
        life_events: serializeLifeEvents(form.life_events) || (typeof form.life_events === "string" ? form.life_events || null : null),
        upi_txn_ref: txnRef || null,
        payment_screenshot_base64: base64,
        first_child_dob: form.first_child_dob || null,
        first_child_tob: form.first_child_tob || null,
        second_child_dob: form.second_child_dob || null,
        second_child_tob: form.second_child_tob || null,
      };
      const res = await api.post("/bookings", body);
      nav("/success", { state: { booking: res.data } });
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail
          ? typeof e2.response.data.detail === "string"
            ? e2.response.data.detail
            : "Please fill all required fields correctly."
          : "Something went wrong. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`section ${lang === "hi" ? "font-devnagri" : ""}`}
      data-testid="payment-page"
    >
      <div className="container-narrow max-w-3xl">
        <div className="text-center mb-10">
          <span className="gold-divider mb-4">Step 2 of 2</span>
          <h1 className="font-display text-4xl md:text-5xl gold-text mb-3">
            {t.paymentTitle}
          </h1>
          <p className="text-[color:var(--cream)] opacity-85">{t.paymentSub}</p>
        </div>

        <div className="card-glass p-6 md:p-8 mb-6">
          <div className="grid md:grid-cols-[220px_1fr] gap-6 md:gap-8 items-center">
            {/* QR code — works on all UPI apps */}
            <div className="flex flex-col items-center">
              <div
                className="p-3 rounded-xl bg-white"
                data-testid="upi-qr"
                style={{ boxShadow: "0 8px 30px -8px rgba(212,166,74,0.4)" }}
              >
                <QRCodeSVG
                  value={upiLink}
                  size={180}
                  bgColor="#ffffff"
                  fgColor="#4a0f18"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--gold-soft)] font-sans-ui mt-3 text-center">
                {lang === "hi"
                  ? "किसी भी UPI ऐप से स्कैन करें"
                  : "Scan with any UPI app"}
              </div>
            </div>

            {/* Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--gold-soft)] mb-1">
                    {t.selectedPackage}
                  </div>
                  <div
                    className="font-display text-lg"
                    style={{ color: "#f6ecd6" }}
                  >
                    {pickLangText(pkg, lang, "title")}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--gold-soft)] mb-1">
                    {t.amount}
                  </div>
                  <div className="price-tag">
                    <span className="rupee">₹</span>
                    <span className="amount" style={{ fontSize: "1.6rem" }}>
                      {amount}
                    </span>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--gold-soft)] mb-1">
                    {t.upiName}
                  </div>
                  <div
                    className="font-display text-base"
                    style={{ color: "#f6ecd6" }}
                  >
                    {upiName}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--gold-soft)] mb-1">
                    UPI ID
                  </div>
                  <div
                    className="flex items-center gap-2 cursor-pointer group"
                    onClick={copyUpi}
                    data-testid="upi-copy"
                  >
                    <span
                      className="font-mono text-[color:var(--gold)] text-base tracking-wide break-all"
                      data-testid="upi-number"
                    >
                      {upiVpa}
                    </span>
                    <button
                      type="button"
                      aria-label="copy"
                      className="text-[color:var(--gold-soft)] group-hover:text-[color:var(--gold)] shrink-0"
                    >
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              {isMobile && (
                <a
                  href={upiLink}
                  className="btn-gold w-full sm:w-auto"
                  data-testid="upi-open-btn"
                >
                  <Smartphone size={14} /> {t.payVia}
                </a>
              )}

              <div className="text-xs text-[color:var(--cream)] opacity-70 font-sans-ui">
                PhonePe · Google Pay · Paytm · BHIM · Amazon Pay — सभी काम करते हैं
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="card-glass p-6 md:p-8 space-y-5">
          <div className="field">
            <label>{t.txnRef}</label>
            <input
              className="input"
              value={txnRef}
              onChange={(e) => setTxnRef(e.target.value)}
              placeholder="XXXXXXXXXXXX"
              data-testid="input-txn-ref"
            />
          </div>

          <div className="field">
            <label>{t.uploadScreenshot}</label>
            <label
              className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-[rgba(212,166,74,0.55)] cursor-pointer hover:bg-[rgba(212,166,74,0.06)]"
              data-testid="upload-label"
            >
              <Upload className="text-[color:var(--gold)]" size={20} />
              <div className="text-sm text-[color:var(--cream)] opacity-90">
                {file ? file.name : t.uploadScreenshot}
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="input-screenshot"
              />
            </label>
          </div>

          {err && (
            <div className="notice error" data-testid="payment-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            className="btn-gold w-full sm:w-auto"
            disabled={busy}
            data-testid="submit-booking-btn"
          >
            {busy ? t.submitting : t.submitBooking}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Payment;
